'use client';

import { Children, useEffect } from 'react';
import PropTypes from 'prop-types';

// Drop-in replacement for react-helmet's <Helmet title>...</Helmet> API, used
// only for the client-side title/meta/script tags it manages (react-helmet is
// unmaintained and its defaultProps-on-function-component pattern isn't
// supported by React 19). Supports only the subset actually used in this
// codebase: the `title` prop and <meta>/<link>/<script> children.

const upsertHead = (child) => {
  const { type, props } = child;
  if (type !== 'meta' && type !== 'link' && type !== 'script') return null;

  const { children: text, dangerouslySetInnerHTML, ...attrs } = props;
  const identifyingAttr = ['name', 'property', 'rel', 'id'].find((key) => attrs[key]);
  const selector = identifyingAttr
    ? `${type}[${identifyingAttr}="${attrs[identifyingAttr]}"]`
    : null;

  const existing = selector ? document.head.querySelector(selector) : null;
  const el = existing || document.createElement(type);

  Object.entries(attrs).forEach(([key, value]) => {
    if (value === undefined || value === null) return;
    el.setAttribute(key, value);
  });
  if (text !== undefined) {
    el.textContent = text;
  } else if (dangerouslySetInnerHTML?.__html !== undefined) {
    el.innerHTML = dangerouslySetInnerHTML.__html;
  }

  if (!existing) document.head.appendChild(el);

  return { el, created: !existing };
};

const Helmet = ({ title, children }) => {
  const tagKey = Children.toArray(children)
    .filter(Boolean)
    .map((child) => JSON.stringify({ type: child.type, props: child.props }))
    .join('|');

  useEffect(() => {
    const previousTitle = document.title;
    if (title) document.title = title;

    const managed = Children.toArray(children)
      .filter(Boolean)
      .map(upsertHead)
      .filter(Boolean);

    return () => {
      if (title) document.title = previousTitle;
      managed.forEach(({ el, created }) => {
        if (created) el.remove();
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, tagKey]);

  return null;
};

Helmet.propTypes = {
  title: PropTypes.string,
  children: PropTypes.node,
};

export default Helmet;
